import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CASE_TYPES, inr } from "@/lib/constants";
import { formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, ShieldCheck, IndianRupee, FileText, UploadCloud } from "lucide-react";

const STEPS = ["Type", "Describe", "Respondent", "Claim", "Evidence", "Book"];

export default function FileCase() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    type: "", title: "", description: "",
    respondent_name: "", respondent_contact: "",
    claim_amount: 0, resolution_method: "mediation",
    preferred_slot: "", consent: false,
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return !!form.type;
    if (step === 1) return form.title.length > 3 && form.description.length > 10;
    if (step === 2) return form.respondent_name && form.respondent_contact;
    if (step === 3) return form.claim_amount > 0;
    return true;
  };
  const canSubmit = form.consent;

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/cases", form);
      // upload files
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/cases/${data.id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Appointment requested. Our team will contact you to confirm the slot and offline payment.");
      nav(`/app/cases/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="file-case-wizard">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-court">File a new dispute</h1>
        <p className="text-muted-foreground">We'll walk you through it step by step.</p>
      </div>
      {/* Stepper */}
      <div className="flex items-center justify-between mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center" data-testid={`wizard-step-${i}`}>
            <div className={`h-8 w-8 rounded-sm grid place-items-center text-xs font-semibold ${i <= step ? "bg-primary text-court-foreground" : "bg-muted text-muted-foreground"}`}>{i + 1}</div>
            {i < STEPS.length - 1 && <div className={`h-0.5 w-6 sm:w-10 mx-1 ${i < step ? "bg-primary" : "bg-border"}`} />}
          </div>
        ))}
      </div>

      <div className="bg-card rounded-sm border border-stone/60 p-8 ">
        {step === 0 && (
          <div className="space-y-4">
            <h3 className="font-display text-xl text-court">What kind of dispute is this?</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {CASE_TYPES.map((t) => (
                <button key={t.value} data-testid={`type-${t.value}`} onClick={() => set("type", t.value)} className={`p-4 rounded-sm border text-left transition ${form.type === t.value ? "border-primary ring-2 ring-primary/20 bg-primary/5" : "border-stone/60 bg-card hover:border-primary/30"}`}>
                  <div className="font-semibold">{t.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-display text-xl text-court">Describe your case</h3>
            <div className="space-y-1.5"><Label>Title</Label><Input data-testid="wizard-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="h-12 rounded-sm" placeholder="Short summary, e.g. Defective laptop not refunded" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea data-testid="wizard-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="min-h-[140px] rounded-sm" placeholder="What happened? Dates, amounts, communications…" /></div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-display text-xl text-court">Respondent details</h3>
            <div className="space-y-1.5"><Label>Name of person / business</Label><Input data-testid="wizard-respondent-name" value={form.respondent_name} onChange={(e) => set("respondent_name", e.target.value)} className="h-12 rounded-sm" /></div>
            <div className="space-y-1.5"><Label>Contact (email or phone)</Label><Input data-testid="wizard-respondent-contact" value={form.respondent_contact} onChange={(e) => set("respondent_contact", e.target.value)} className="h-12 rounded-sm" /></div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-display text-xl text-court">Claim amount &amp; method</h3>
            <div className="space-y-1.5">
              <Label>Claim amount (₹)</Label>
              <div className="relative">
                <IndianRupee className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="wizard-claim-amount" type="number" min={0} value={form.claim_amount} onChange={(e) => set("claim_amount", Number(e.target.value))} className="h-12 rounded-sm pl-9" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Resolution method</Label>
              <Select value={form.resolution_method} onValueChange={(v) => set("resolution_method", v)}>
                <SelectTrigger data-testid="wizard-method-trigger" className="h-12 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mediation">Online Mediation</SelectItem>
                  <SelectItem value="arbitration">Online Arbitration</SelectItem>
                  <SelectItem value="conciliation">Conciliation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {step === 4 && (
          <div className="space-y-4">
            <h3 className="font-display text-xl text-court">Upload supporting evidence</h3>
            <label className="block border-2 border-dashed border-stone/60 rounded-sm p-10 text-center cursor-pointer hover:border-primary/40 transition" data-testid="wizard-upload-area">
              <UploadCloud className="h-8 w-8 text-court mx-auto mb-2" />
              <div className="font-medium">Drop files or click to browse</div>
              <div className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG up to 10MB each</div>
              <input data-testid="wizard-file-input" type="file" multiple className="hidden" onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])} />
            </label>
            {files.length > 0 && (
              <ul className="space-y-2 mt-4">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm bg-muted rounded-lg px-3 py-2">
                    <FileText className="h-4 w-4 text-court" /> {f.name} <span className="text-muted-foreground text-xs">({Math.round(f.size/1024)} KB)</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {step === 5 && (
          <div className="space-y-5" data-testid="wizard-payment">
            <h3 className="font-display text-xl text-court">Book your consultation</h3>
            <div className="rounded-sm bg-primary/5 border border-primary/20 p-6">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Consultation fee</div>
              <div className="font-display text-4xl font-bold text-court mt-1">{inr(2500)}</div>
              <p className="text-sm text-muted-foreground mt-2">60-minute expert consultation + suitability assessment for ODR.</p>
              <div className="mt-4 rounded-sm bg-card border border-stone/60 p-3 flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                <div className="text-xs text-muted-foreground">
                  Payment is collected <span className="font-medium text-ink">offline</span>. Once you confirm, our team will reach out to lock in your slot and arrange the ₹2,500 payment directly with you.
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Preferred slot (optional)</Label>
              <Input
                data-testid="wizard-preferred-slot"
                type="datetime-local"
                value={form.preferred_slot}
                onChange={(e) => set("preferred_slot", e.target.value)}
                className="h-12 rounded-sm"
              />
              <p className="text-xs text-muted-foreground">We'll do our best to accommodate — the team will confirm over call/email.</p>
            </div>

            <label className="flex items-start gap-3 text-sm text-muted-foreground select-none">
              <input
                type="checkbox"
                data-testid="wizard-consent"
                checked={form.consent}
                onChange={(e) => set("consent", e.target.checked)}
                className="mt-1 h-4 w-4 accent-[hsl(17_64%_47%)]"
              />
              <span>
                I understand eSulah is <span className="text-ink font-medium">not a law firm</span>. I agree to be contacted by the eSulah team to confirm my consultation slot and to pay the ₹2,500 fee <span className="text-ink font-medium">offline</span>.
              </span>
            </label>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep(s => s - 1)} className="rounded-sm" data-testid="wizard-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button disabled={!canNext()} onClick={() => setStep(s => s + 1)} className="rounded-sm bg-primary" data-testid="wizard-next">
            Next <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button disabled={submitting || !canSubmit} onClick={onSubmit} className="rounded-sm bg-secondary hover:bg-secondary/90" data-testid="wizard-submit">
            {submitting ? "Submitting…" : "Confirm appointment"}
          </Button>
        )}
      </div>
    </div>
  );
}
